"use client";

import type { AboutUsTeamMember } from "@homeinstead/schemas";

import Arrow from "@homeinstead/icons/Arrow";
import Close from "@homeinstead/icons/Close";
import { cn } from "@homeinstead/ui";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerOverlay,
  DrawerTitle,
} from "@homeinstead/ui/components/Drawer";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { buildTeamMemberBioHref } from "~www/lib/about-us/build-team-member-bio-href";
import { getSearchParamsFromUrlSearchParams } from "~www/lib/search-params";
import { AboutUsTeamMemberPhoto } from "./AboutUsTeamMemberPhoto";
import { getTeamMemberBioDrawerPresentation } from "./get-team-member-bio-drawer-presentation";

const drawerAnimationClassName =
  "[animation-duration:200ms] [transition-duration:200ms]";

const drawerIconButtonClassName = cn(
  "flex h-10 w-10 items-center justify-center rounded-full",
  "hover:bg-new-beginnings-tints-800",
  "focus:outline focus:ring-2 focus:ring-new-beginnings-base focus:ring-offset-4",
);

const drawerCloseButtonClassName = cn(
  "inline-flex h-10 items-center gap-2 rounded-full px-3 font-bold text-base text-new-beginnings-base",
  "hover:bg-new-beginnings-tints-800",
  "focus:outline focus:ring-2 focus:ring-new-beginnings-base focus:ring-offset-4",
);

type TeamMemberBioDrawerProps = {
  basePath?: string;
  teamMembers: AboutUsTeamMember[];
};

type TeamMemberBioDrawerContentProps = {
  activeMember: AboutUsTeamMember;
  isOpen: boolean;
  onNavigateToMember: (memberId: string | undefined) => void;
  onRequestClose: () => void;
  teamMembers: AboutUsTeamMember[];
};

function TeamMemberBioDrawerContent({
  activeMember,
  isOpen,
  onNavigateToMember,
  onRequestClose,
  teamMembers,
}: TeamMemberBioDrawerContentProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const activeIndex = teamMembers.findIndex(
    (member) => member.id === activeMember.id,
  );
  const previousMember =
    activeIndex > 0 ? teamMembers[activeIndex - 1] : undefined;
  const nextMember =
    activeIndex >= 0 && activeIndex < teamMembers.length - 1
      ? teamMembers[activeIndex + 1]
      : undefined;

  useEffect(() => {
    scrollContainerRef.current?.scrollTo?.({ top: 0 });
  }, [activeMember.id]);

  return (
    <Drawer
      direction={isDesktop ? "right" : "bottom"}
      fixed
      noBodyStyles
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          return;
        }

        onRequestClose();
      }}
      open={isOpen}
    >
      <DrawerOverlay
        className={cn("z-[250] bg-black/40", drawerAnimationClassName)}
      />
      <DrawerContent
        className={cn(
          "z-[251] bg-support-tints-400 md:left-auto",
          drawerAnimationClassName,
        )}
      >
        <DrawerTitle className="sr-only">{activeMember.name}</DrawerTitle>
        <DrawerDescription className="sr-only">
          Biography for {activeMember.name}
          {!activeMember.jobTitle ? null : `, ${activeMember.jobTitle}`}.
        </DrawerDescription>

        <div className="flex h-20 shrink-0 items-center justify-between border-new-beginnings-tints-600 border-b px-8">
          <DrawerClose asChild>
            <button className={drawerCloseButtonClassName} type="button">
              <Close className="size-4" />
              Close
            </button>
          </DrawerClose>

          <div className="flex items-center gap-4">
            <button
              aria-label="Previous team member"
              className={cn(
                drawerIconButtonClassName,
                !previousMember ? "pointer-events-none opacity-40" : null,
              )}
              disabled={!previousMember}
              onClick={() => onNavigateToMember(previousMember?.id)}
              type="button"
            >
              <Arrow className="size-4 rotate-90 fill-new-beginnings-base stroke-[2px] stroke-new-beginnings-base" />
            </button>
            <button
              aria-label="Next team member"
              className={cn(
                drawerIconButtonClassName,
                !nextMember ? "pointer-events-none opacity-40" : null,
              )}
              disabled={!nextMember}
              onClick={() => onNavigateToMember(nextMember?.id)}
              type="button"
            >
              <Arrow className="size-4 -rotate-90 fill-new-beginnings-base stroke-[2px] stroke-new-beginnings-base" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto" ref={scrollContainerRef}>
          <div className="mx-auto max-w-[520px] px-8 py-8">
            <div className="flex flex-col gap-6">
              <div className="space-y-1">
                <p aria-hidden className="font-bold text-black text-lg">
                  {activeMember.name}
                </p>
                {!activeMember.jobTitle ? null : (
                  <p className="font-bold text-new-beginnings-base text-sm leading-6">
                    {activeMember.jobTitle}
                  </p>
                )}
              </div>

              <AboutUsTeamMemberPhoto member={activeMember} size="drawer" />

              {!activeMember.bio ? null : (
                <p className="max-w-none font-medium text-base text-new-beginnings-base leading-7">
                  {activeMember.bio}
                </p>
              )}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function TeamMemberBioDrawer({
  basePath,
  teamMembers,
}: TeamMemberBioDrawerProps) {
  const pathname = usePathname();
  const router = useRouter();
  const urlSearchParams = useSearchParams();
  const isClosingRef = useRef(false);
  const activeMemberId = urlSearchParams.get("bio");
  const [pendingNavigation, setPendingNavigation] = useState<{
    fromMemberId: string | null;
    toMemberId: string | null;
  }>({
    fromMemberId: null,
    toMemberId: null,
  });
  const [closingMemberId, setClosingMemberId] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [suppressedNavigationMemberId, setSuppressedNavigationMemberId] =
    useState<string | null>(null);
  const { displayedMemberId, isOpen } = getTeamMemberBioDrawerPresentation({
    activeMemberId,
    closingMemberId,
    isClosing,
    pendingNavigation,
    suppressedNavigationMemberId,
  });
  const searchParams = useMemo(
    () => getSearchParamsFromUrlSearchParams(urlSearchParams),
    [urlSearchParams],
  );

  const navigateToPath = useCallback(
    (href: string) => {
      router.replace(href.startsWith("?") ? `${pathname}${href}` : href, {
        scroll: false,
      });
    },
    [pathname, router],
  );

  const buildPathWithoutBio = useCallback(() => {
    const params = new URLSearchParams(urlSearchParams.toString());
    params.delete("bio");

    const queryString = params.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
  }, [pathname, urlSearchParams]);

  const clearPendingClose = useCallback(() => {
    isClosingRef.current = false;
    setIsClosing(false);
    setClosingMemberId(null);
    setSuppressedNavigationMemberId(null);
  }, [setClosingMemberId, setIsClosing, setSuppressedNavigationMemberId]);

  const navigateToMember = useCallback(
    (memberId: string | undefined) => {
      if (!memberId) {
        return;
      }

      clearPendingClose();
      setPendingNavigation({
        fromMemberId: activeMemberId,
        toMemberId: memberId,
      });

      const href = buildTeamMemberBioHref({
        basePath: basePath ?? pathname,
        memberId,
        searchParams,
      });

      navigateToPath(href);
    },
    [
      activeMemberId,
      basePath,
      clearPendingClose,
      navigateToPath,
      pathname,
      searchParams,
      setPendingNavigation,
    ],
  );

  const beginClose = useCallback(() => {
    if (isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    setIsClosing(true);
    setClosingMemberId(activeMemberId);
    setSuppressedNavigationMemberId(pendingNavigation.toMemberId);
    setPendingNavigation({
      fromMemberId: null,
      toMemberId: null,
    });
    navigateToPath(buildPathWithoutBio());
  }, [
    activeMemberId,
    buildPathWithoutBio,
    navigateToPath,
    pendingNavigation.toMemberId,
    setClosingMemberId,
    setIsClosing,
    setPendingNavigation,
    setSuppressedNavigationMemberId,
  ]);

  useEffect(() => {
    if (
      pendingNavigation.fromMemberId === null &&
      pendingNavigation.toMemberId === null
    ) {
      return;
    }

    if (
      activeMemberId === pendingNavigation.toMemberId ||
      activeMemberId !== pendingNavigation.fromMemberId
    ) {
      queueMicrotask(() => {
        setPendingNavigation({
          fromMemberId: null,
          toMemberId: null,
        });
      });
    }
  }, [
    activeMemberId,
    pendingNavigation.fromMemberId,
    pendingNavigation.toMemberId,
  ]);

  useEffect(() => {
    if (!isClosing || activeMemberId === null) {
      return;
    }

    const isWaitingForUrlToClear =
      suppressedNavigationMemberId === null &&
      activeMemberId === closingMemberId;

    if (isWaitingForUrlToClear) {
      return;
    }

    const isSuppressedLateNavigationTarget =
      suppressedNavigationMemberId !== null &&
      activeMemberId === suppressedNavigationMemberId;

    if (isSuppressedLateNavigationTarget) {
      return;
    }

    queueMicrotask(() => {
      clearPendingClose();
    });
  }, [
    activeMemberId,
    clearPendingClose,
    closingMemberId,
    isClosing,
    suppressedNavigationMemberId,
  ]);

  useEffect(() => {
    if (!isClosing) {
      return;
    }

    if (activeMemberId !== null) {
      return;
    }

    queueMicrotask(() => {
      clearPendingClose();
    });
  }, [activeMemberId, clearPendingClose, isClosing]);

  const activeMember = teamMembers.find(
    (member) => member.id === displayedMemberId,
  );

  if (!activeMember) {
    return null;
  }

  return (
    <TeamMemberBioDrawerContent
      activeMember={activeMember}
      isOpen={isOpen}
      onNavigateToMember={navigateToMember}
      onRequestClose={beginClose}
      teamMembers={teamMembers}
    />
  );
}

export { TeamMemberBioDrawer };
